import React from 'react';
import { useApp } from '../context/AppContext';
import { formatCurrency, formatCurrencyPrecise, priceBreakdown, formatDate } from '../lib/format';
import { LimitedTimeOffer } from './LimitedTimeOffer';
import {
  ShieldCheck,
  Lock,
  ArrowRight,
  ClipboardList,
  Smartphone,
  FileText,
  MessageCircle,
  CheckCircle2,
} from 'lucide-react';

/** The four steps between tapping the button and being in the group.
 *  The step number is drawn on the badge, so it is not repeated in the title. */
const STEPS = [
  {
    icon: ClipboardList,
    title: 'Fill in Your Details',
    body: 'Enter your name, WhatsApp number, and email address (takes under a minute).',
  },
  {
    icon: Smartphone,
    title: 'Complete Your Payment',
    body: 'Pay securely via UPI, card, or netbanking on the Cashfree checkout page.',
  },
  {
    icon: FileText,
    title: 'Access Your Link & Invoice',
    body: 'Receive your GST invoice and access the WhatsApp group link instantly upon payment confirmation.',
  },
  {
    icon: MessageCircle,
    title: 'Join the WhatsApp Group',
    body: 'Click the provided group link to enter and join the community directly.',
  },
];

/**
 * Checkout, at the end of the scroll.
 *
 * Everything above this has been the pitch; this is the only place on the page
 * that asks for money. The button opens the details form, which writes the lead
 * before the visitor leaves for the gateway — so an abandoned checkout is still a
 * lead in the admin console, not a lost click.
 */
export const EnrollSection = ({ workshop }) => {
  const { openRegistration, adminSettings, navigate } = useApp();

  const { net, gst, total } = priceBreakdown(workshop.price, 0);
  const seats = Number(workshop.seatsLeft) || 0;

  return (
    <section id="enroll" className="relative section bg-ink-950 text-white overflow-hidden grain">
      <div className="absolute inset-x-0 top-0 rule-brass" aria-hidden="true" />
      <div
        className="absolute -bottom-28 -left-20 w-80 h-80 rounded-full blur-[100px] opacity-40 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(95,37,159,0.6) 0%, transparent 70%)' }}
        aria-hidden="true"
      />

      <div className="shell relative z-10">
        <span className="pill glass-dark text-gold-300">Book your seat</span>
        <h2 className="mt-3.5 font-display text-display-md font-bold text-balance">
          {workshop.title}
        </h2>
        <p className="mt-2.5 text-sm text-white/55">
          Live from {formatDate(workshop.date)} · {workshop.time}
        </p>

        {/* ── Price ───────────────────────────────────────────────────────── */}
        <div className="mt-6 rounded-3xl border border-white/12 bg-white/[0.04] backdrop-blur-sm p-5">
          <LimitedTimeOffer workshop={workshop} />

          <dl className="space-y-2.5 text-sm">
            <div className="flex justify-between text-white/60">
              <dt>Workshop fee</dt>
              <dd className="font-medium text-white/90" data-numeric>
                {formatCurrency(net)}
              </dd>
            </div>
            <div className="flex justify-between text-white/60">
              <dt>GST (18%)</dt>
              <dd className="font-medium text-white/90" data-numeric>
                {formatCurrencyPrecise(gst)}
              </dd>
            </div>
            <div className="pt-3 mt-1 border-t border-white/10 flex justify-between items-baseline">
              <dt className="font-bold text-white">Total payable</dt>
              <dd
                className="font-sans text-2xl font-extrabold tracking-[-0.03em] text-gold-300"
                data-numeric
              >
                {formatCurrencyPrecise(total)}
              </dd>
            </div>
          </dl>

          <button
            onClick={() => openRegistration(workshop)}
            className="btn btn-primary btn-lg w-full mt-5"
          >
            <Lock className="w-4 h-4" />
            Pay {formatCurrencyPrecise(total)}
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="mt-3.5 flex items-center justify-center gap-4 text-[11px] text-white/45">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-pay-400" />
              Cashfree secured
            </span>
            {seats > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse" />
                {seats} seats left
              </span>
            )}
          </div>
        </div>

        {/* ── What happens next ───────────────────────────────────────────── */}
        <ol className="mt-8 space-y-3.5">
          {STEPS.map(({ icon: Icon, title, body }, i) => (
            <li key={title} className="flex gap-3.5">
              <span className="relative w-9 h-9 shrink-0 rounded-xl glass-dark flex items-center justify-center">
                <Icon className="w-4 h-4 text-gold-400" />
                <span className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-brand-600 text-[10px] font-bold flex items-center justify-center border border-ink-950">
                  {i + 1}
                </span>
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold">{title}</p>
                <p className="text-[0.78rem] text-white/50 leading-relaxed mt-0.5">{body}</p>
              </div>
            </li>
          ))}
        </ol>

        {/* ── What the fee includes ───────────────────────────────────────── */}
        {Array.isArray(workshop.highlights) && workshop.highlights.length > 0 && (
          <ul className="mt-8 grid gap-2.5">
            {workshop.highlights.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-[0.82rem] text-white/70">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        )}

        <p className="mt-8 text-[11px] text-white/35 leading-relaxed text-center">
          Paid to {adminSettings.legalName}. By booking you accept our{' '}
          <a
            href="/terms"
            onClick={(event) => {
              event.preventDefault();
              navigate('/terms');
            }}
            className="text-white/60 underline underline-offset-2"
          >
            terms
          </a>{' '}
          and{' '}
          <a
            href="/refund-policy"
            onClick={(event) => {
              event.preventDefault();
              navigate('/refund-policy');
            }}
            className="text-white/60 underline underline-offset-2"
          >
            refund policy
          </a>
          . We never see or store your card details.
        </p>
      </div>
    </section>
  );
};
